import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@parrot-software/design-system';
import { XCircle as XCircleIcon } from '@parrot-software/design-system/icons';
import type { ReportFilterConfig } from '@reports/domain/value-objects';
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR, formatHour12 } from '@reports/presentation/utils';

/**
 * Applied filters structure for report filter badges
 */
interface AppliedFiltersData {
  startHour: string;
  endHour: string;
  filters: Readonly<Record<string, ReadonlyArray<string>>>;
}

interface ReportFilterBadgesProps {
  isFiltersLoading: boolean;
  appliedFilters: AppliedFiltersData;
  filterConfigs: ReadonlyArray<ReportFilterConfig>;
  onBadgeClick: () => void;
  onRemoveFilter: (filterKey: string) => void;
  onRemoveHours: () => void;
}

/**
 * ReportFilterBadges - Displays applied filters as badges with counts
 *
 * Shows a badge for each filter category that has selected values.
 * Each badge displays the filter label and the count of selected items.
 * Includes a badge for custom hour ranges when different from default (00:00:00 - 23:59:59).
 * The hours badge is always displayed first when present.
 * Clicking a badge opens the filter sheet, clicking the X removes that filter category.
 *
 * @param {ReportFilterBadgesProps} props - Component properties
 * @param {boolean} props.isFiltersLoading - Whether the filters catalog is loading
 * @param {AppliedFiltersData} props.appliedFilters - Complete applied filters including hours and filter categories
 * @param {ReadonlyArray<ReportFilterConfig>} props.filterConfigs - Filter configurations for labels
 * @param {Function} props.onBadgeClick - Callback when badge is clicked (opens sheet)
 * @param {Function} props.onRemoveFilter - Callback when X is clicked (removes filter category)
 * @param {Function} props.onRemoveHours - Callback when X is clicked on hours badge (resets to default hours)
 * @returns {React.ReactElement} Filter badges component
 *
 * @example
 * <ReportFilterBadges
 *   isFiltersLoading={false}
 *   appliedFilters={{
 *     startHour: '09:00:00',
 *     endHour: '18:00:00',
 *     filters: { brands: ['brand1', 'brand2'], providers: ['provider1'] }
 *   }}
 *   filterConfigs={reportDefinition.filters}
 *   onBadgeClick={handleOpenSheet}
 *   onRemoveFilter={handleRemoveFilter}
 *   onRemoveHours={handleRemoveHours}
 * />
 */
export const ReportFilterBadges = ({
  isFiltersLoading,
  appliedFilters,
  filterConfigs,
  onBadgeClick,
  onRemoveFilter,
  onRemoveHours,
}: ReportFilterBadgesProps) => {
  const { t } = useTranslation();

  const handleRemoveClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, filterKey: string) => {
      event.stopPropagation();
      onRemoveFilter(filterKey);
    },
    [onRemoveFilter],
  );

  const handleRemoveHoursClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onRemoveHours();
    },
    [onRemoveHours],
  );

  // Check if custom hours are applied (different from default)
  const hasCustomHours = appliedFilters.startHour !== DEFAULT_START_HOUR || appliedFilters.endHour !== DEFAULT_END_HOUR;

  // Get filter configs that have applied values
  const activeFilters = filterConfigs.filter((config) => {
    const filterValues = appliedFilters.filters[config.key];
    return filterValues && filterValues.length > 0;
  });

  // Show badges if there are active filters or custom hours
  if ((activeFilters.length === 0 && !hasCustomHours) || isFiltersLoading) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] scrollbar-none">
      {/* Hours badge - always first when present */}
      {hasCustomHours && (
        <Badge
          className="flex items-center gap-1 px-2 py-0.5 cursor-pointer whitespace-nowrap shrink-0"
          key="hours"
          onClick={onBadgeClick}
          variant="outline"
        >
          <span className="text-xs font-medium">
            {`${formatHour12(appliedFilters.startHour)} - ${formatHour12(appliedFilters.endHour)}`}
          </span>
          <button
            aria-label={`Remove ${t('reportCommon:labels.hours')} filter`}
            className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-gray-200 transition-colors ml-0.5"
            onClick={handleRemoveHoursClick}
            type="button"
          >
            <XCircleIcon size={12} />
          </button>
        </Badge>
      )}

      {/* Other filter badges */}
      {activeFilters.map((filterConfig) => {
        const filterValues = appliedFilters.filters[filterConfig.key];
        const count = filterValues?.length || 0;

        return (
          <Badge
            className="flex items-center gap-1 px-2 py-0.5 cursor-pointer whitespace-nowrap shrink-0"
            key={filterConfig.key}
            onClick={onBadgeClick}
            variant="outline"
          >
            <span className="text-xs font-medium">
              {t(filterConfig.label)} ({count})
            </span>
            <button
              aria-label={`Remove ${t(filterConfig.label)} filter`}
              className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-gray-200 transition-colors ml-0.5"
              onClick={(e) => handleRemoveClick(e, filterConfig.key)}
              type="button"
            >
              <XCircleIcon size={12} />
            </button>
          </Badge>
        );
      })}
    </div>
  );
};
